// Master Apps · Gate de autorização por papel (role).
// Roda SEMPRE por dentro do <Protected> do app.tsx, ou seja depois de sessão
// (RequireAuth) + aprovação/onboarding (AccessGate): quando chega aqui o
// usuário já está logado, aprovado e com matrícula, falta só o papel.
// Sem papel de administrador a tela administrativa nunca é renderizada (nem
// dispara as queries dela), e o usuário vê a tela de acesso restrito.
import type { ReactElement } from 'react';
import { Navigate, useNavigate } from 'react-router';
import {
  AuthCardBody,
  AuthShell,
  primaryButtonStyle,
  SUPPORT_URL,
  secondaryButtonStyle,
} from '@/components/auth-shell';
import { IconExternal, IconGrid, IconShield } from '@/components/icons';
import { ScreenLoading } from '@/components/loading';
import { useMeFresh } from '@/hooks/use-applications';
import { makePalette } from '@/lib/theme';
import { useScreenProps } from '@/store/theme';

// Tela cheia (sem sidebar, igual /aguardando) para quem tentou abrir uma área
// administrativa pela URL. Mesma identidade visual do AuthShell: brilho radial
// no acento Masterboi, marca no topo e cartão central.
export function ScreenAccessDenied() {
  const sp = useScreenProps();
  const p = makePalette(sp.dark);
  const navigate = useNavigate();

  return (
    <AuthShell>
      <AuthCardBody
        badge={<IconShield size={24} stroke={2} />}
        title="Acesso restrito"
        description={
          <>
            Esta área é exclusiva dos administradores do Master Apps. Seu usuário está liberado para
            o catálogo, mas não tem permissão de administrador. Se você precisa desse acesso para o
            seu trabalho, fale com a TI.
          </>
        }
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate('/catalog')}
              style={primaryButtonStyle(sp.accent)}
            >
              <IconGrid size={14} />
              Voltar ao catálogo
            </button>
            <a
              href={SUPPORT_URL}
              target="_blank"
              rel="noreferrer"
              style={{ ...secondaryButtonStyle(p), textDecoration: 'none' }}
            >
              <IconExternal size={14} />
              Falar com a TI
            </a>
          </>
        }
      />
    </AuthShell>
  );
}

export function RequireAdmin({ children }: Readonly<{ children: ReactElement }>) {
  const sp = useScreenProps();
  // Mesma query ['me'] do AccessGate (useMeFresh): aqui o dado já está no
  // cache, então na prática não há segundo spinner.
  const { data: me, isError } = useMeFresh();

  if (!me) {
    // Falha de /api/me já é tratada pelo AccessGate acima (tela com retry);
    // se ainda assim chegar aqui sem perfil, o gate fecha e devolve o usuário
    // ao catálogo em vez de liberar a área administrativa.
    if (isError) return <Navigate to="/catalog" replace />;
    return <ScreenLoading dark={sp.dark} />;
  }
  if (me.role === 'admin') return children;
  return <ScreenAccessDenied />;
}
